import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import { getUserProfile } from "../../utils/auth";
import {
  BatchesContainer,
  BatchesHeader,
  FiltersContainer,
  FiltersForm,
  FilterField,
  FilterInput,
  FilterSelect,
  FilterButton,
  BatchesTable,
  BatchRow,
  InfoBadge,
  ConsultationsTable,
  SectionTitle,
  EmptyState,
  Loader,
  PaginationContainer,
  PaginationInfo,
  PaginationControls,
  PageButton,
  PageSizeSelect,
} from "./BatchesPage.styles";

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString("pt-BR", { hour12: false });
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("pt-BR");
};

const BatchesPage = () => {
  const [batches, setBatches] = useState([]);
  const [consultas, setConsultas] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingConsultas, setLoadingConsultas] = useState(false);
  const [error, setError] = useState("");
  const [municipios, setMunicipios] = useState([]);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  
  // Pagination State for Batches
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Pagination State for Consultas
  const [consultasPage, setConsultasPage] = useState(1);
  const [consultasPageSize, setConsultasPageSize] = useState(10);
  const [consultasTotalRecords, setConsultasTotalRecords] = useState(0);
  const [consultasTotalPages, setConsultasTotalPages] = useState(1);

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    cidade: "",
  });

  const isAdmin = getUserProfile() === "Administrador";

  const fetchBatches = async (pageIdx = page, size = pageSize, filterValues = filters) => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page: pageIdx,
        page_size: size,
      };
      if (filterValues.startDate) {
        params.start_date = filterValues.startDate;
      }
      if (filterValues.endDate) {
        params.end_date = filterValues.endDate;
      }
      if (filterValues.cidade) {
        params.cidade = filterValues.cidade;
      }

      const response = await api.get("/extrator/importacao/batches", { params });
      
      // Handle new response format { data: [...], page: 1, ... }
      if (response.data && response.data.data) {
        setBatches(response.data.data);
        setTotalRecords(response.data.total_records || 0);
        setTotalPages(response.data.total_pages || 1);
        
        if (response.data.data.length > 0) {
          setSelectedBatch(response.data.data[0]);
        } else {
          setSelectedBatch(null);
        }
      } else {
        // Fallback for empty or legacy
        setBatches([]);
        setSelectedBatch(null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao carregar batches.");
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchConsultas = async (batchId, pageIdx = consultasPage, size = consultasPageSize) => {
    if (!batchId) {
      setConsultas([]);
      setConsultasTotalRecords(0);
      setConsultasTotalPages(1);
      return;
    }

    setLoadingConsultas(true);
    setError("");

    try {
      const response = await api.get(`/extrator/importacao/batches/${batchId}/consultas`, {
        params: {
          page: pageIdx,
          page_size: size,
        },
      });

      // Handle new response format { data: [...], page: 1, ... }
      if (response.data && response.data.data) {
        setConsultas(response.data.data);
        setConsultasTotalRecords(response.data.total_records || 0);
        setConsultasTotalPages(response.data.total_pages || 1);
      } else {
        // Fallback or empty
        setConsultas([]);
        setConsultasTotalRecords(0);
        setConsultasTotalPages(1);
      }
    } catch (err) {
      setConsultas([]);
      setConsultasTotalRecords(0);
      setConsultasTotalPages(1);
      setError(err.response?.data?.detail || "Erro ao carregar as consultas do batch.");
    } finally {
      setLoadingConsultas(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchBatches();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;

    const loadMunicipios = async () => {
      try {
        const response = await api.get("/municipios/list", {
          params: { page: 1, size: 900 },
        });
        setMunicipios(response.data);
      } catch (err) {
        console.error("Erro ao carregar municípios:", err);
      }
    };

    loadMunicipios();
  }, [isAdmin]);

  const handleFiltersChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = async (event) => {
    event.preventDefault();
    setIsApplyingFilters(true);
    setPage(1); // Reset to first page on filter
    try {
      await fetchBatches(1, pageSize, filters);
    } finally {
      setIsApplyingFilters(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
    fetchBatches(newPage, pageSize, filters);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
    fetchBatches(1, newSize, filters);
  };

  const handleConsultasPageChange = (newPage) => {
    if (newPage < 1 || newPage > consultasTotalPages) return;
    setConsultasPage(newPage);
    fetchConsultas(selectedBatch?.batch_id, newPage, consultasPageSize);
  };

  const handleConsultasPageSizeChange = (newSize) => {
    setConsultasPageSize(newSize);
    setConsultasPage(1);
    fetchConsultas(selectedBatch?.batch_id, 1, newSize);
  };

  useEffect(() => {
    if (selectedBatch) {
      setConsultasPage(1); // Reset to first page when changing batch
      fetchConsultas(selectedBatch.batch_id, 1, consultasPageSize);
    } else {
      setConsultas([]);
      setConsultasTotalRecords(0);
    }
  }, [selectedBatch]);

  const totalConsultas = useMemo(() => consultasTotalRecords, [consultasTotalRecords]);

  if (!isAdmin) {
    return <EmptyState>Você precisa ser administrador para acessar essa página.</EmptyState>;
  }

  return (
    <BatchesContainer>
      <BatchesHeader>
        <div>
          <h1>Sincronizações do extrator</h1>
          <p>Visualize todos os batches importados e veja os detalhes das consultas por lote.</p>
        </div>
        {loading && <Loader>Buscando lotes...</Loader>}
      </BatchesHeader>

      <FiltersContainer>
        <FiltersForm onSubmit={handleApplyFilters}>
          <FilterField>
            <span>Período inicial</span>
            <FilterInput
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFiltersChange}
            />
          </FilterField>
          <FilterField>
            <span>Período final</span>
            <FilterInput
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFiltersChange}
            />
          </FilterField>
          <FilterField>
            <span>Cidade</span>
            <FilterSelect name="cidade" value={filters.cidade} onChange={handleFiltersChange}>
              <option value="">Todas as cidades</option>
              {municipios.map((municipio) => (
                <option key={municipio.cod_ibge} value={municipio.cod_ibge}>
                  {municipio.nome}
                </option>
              ))}
            </FilterSelect>
          </FilterField>
          <FilterButton type="submit" disabled={isApplyingFilters || loading}>
            {isApplyingFilters ? "Aplicando filtros..." : "Aplicar filtros"}
          </FilterButton>
        </FiltersForm>
      </FiltersContainer>

      {error && <EmptyState>{error}</EmptyState>}

      <BatchesTable>
        <thead>
          <tr>
            <th>Batch</th>
            <th>Gestantes</th>
            <th>Gestações</th>
            <th>Consultas</th>
            <th>Última importação</th>
            <th>Cidade</th>
          </tr>
        </thead>
        <tbody>
          {batches.map((batch) => (
            <BatchRow
              key={batch.batch_id}
              isSelected={selectedBatch?.batch_id === batch.batch_id}
              onClick={() => setSelectedBatch(batch)}
            >
              <td>
                {batch.batch_id}
                {selectedBatch?.batch_id === batch.batch_id && <InfoBadge>Selecionado</InfoBadge>}
              </td>
              <td>{batch.gestantes}</td>
              <td>{batch.gravidezes}</td>
              <td>{batch.consultas}</td>
              <td>{formatDateTime(batch.last_imported_at)}</td>
              <td>{batch.municipio || batch.municipio_cod_ibge || "-"}</td>
            </BatchRow>
          ))}
        </tbody>
      </BatchesTable>
      
      <PaginationContainer>
        <PaginationInfo>
          Mostrando {batches.length} de {totalRecords} registros
        </PaginationInfo>
        <PaginationControls>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginRight: "16px" }}>
            <span style={{ fontSize: "13px", color: "#6c757d" }}>Itens por página:</span>
            <PageSizeSelect
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </PageSizeSelect>
          </div>

          <PageButton disabled={page === 1 || loading} onClick={() => handlePageChange(page - 1)}>
            Anterior
          </PageButton>

          <span style={{ fontSize: "13px", color: "#495057", margin: "0 8px" }}>
            Página {page} de {totalPages || 1}
          </span>

          <PageButton
            disabled={page >= totalPages || loading}
            onClick={() => handlePageChange(page + 1)}
          >
            Próxima
          </PageButton>
        </PaginationControls>
      </PaginationContainer>

      <SectionTitle>
        Consultas do batch {selectedBatch?.batch_id || "-"} ({totalConsultas})
      </SectionTitle>

      {loadingConsultas ? (
        <Loader>Carregando consultas...</Loader>
      ) : consultas.length === 0 ? (
        <EmptyState>Nenhuma consulta encontrada para este lote.</EmptyState>
      ) : (
        <>
          <ConsultationsTable>
            <thead>
              <tr>
                <th>ID</th>
                <th>Gestante</th>
                <th>Data da consulta</th>
                <th>Profissional</th>
              </tr>
            </thead>
            <tbody>
              {consultas.map((consulta) => (
                <tr key={consulta.consulta_id}>
                  <td>{consulta.consulta_id}</td>
                  <td>
                    {consulta.gestante_nome}
                    <br />
                    <small>{consulta.gestante_cpf}</small>
                  </td>
                  <td>{formatDateOnly(consulta.data_consulta)}</td>
                  <td>{consulta.profissional}</td>
                </tr>
              ))}
            </tbody>
          </ConsultationsTable>
          
          <PaginationContainer>
            <PaginationInfo>
              Mostrando {consultas.length} de {consultasTotalRecords} registros
            </PaginationInfo>
            <PaginationControls>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginRight: "16px" }}>
                <span style={{ fontSize: "13px", color: "#6c757d" }}>Itens por página:</span>
                <PageSizeSelect
                  value={consultasPageSize}
                  onChange={(e) => handleConsultasPageSizeChange(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </PageSizeSelect>
              </div>

              <PageButton 
                disabled={consultasPage === 1 || loadingConsultas} 
                onClick={() => handleConsultasPageChange(consultasPage - 1)}
              >
                Anterior
              </PageButton>

              <span style={{ fontSize: "13px", color: "#495057", margin: "0 8px" }}>
                Página {consultasPage} de {consultasTotalPages || 1}
              </span>

              <PageButton
                disabled={consultasPage >= consultasTotalPages || loadingConsultas}
                onClick={() => handleConsultasPageChange(consultasPage + 1)}
              >
                Próxima
              </PageButton>
            </PaginationControls>
          </PaginationContainer>
        </>
      )}
    </BatchesContainer>
  );
};

export default BatchesPage;
